import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { versionFormSchema, type VersionFormData } from '@geely/shared/schemas';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Switch,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@geely/ui";
import type { Version, ModelWithVersions } from '@geely/shared';

interface VersionFormDialogProps {
  isOpen: boolean;
  editingVersion: Version | null;
  models: ModelWithVersions[];
  onSubmit: (data: VersionFormData) => Promise<void>;
  onClose: () => void;
}

const defaultValues: VersionFormData = {
  name: '',
  modelId: '',
  isActive: true,
};

export function VersionFormDialog({
  isOpen,
  editingVersion,
  models,
  onSubmit,
  onClose,
}: VersionFormDialogProps) {
  const form = useForm<VersionFormData>({
    resolver: zodResolver(versionFormSchema),
    defaultValues,
    mode: 'onBlur',
  });

  const { formState: { isSubmitting } } = form;

  useEffect(() => {
    if (isOpen) {
      if (editingVersion) {
        form.reset({
          name: editingVersion.name,
          modelId: editingVersion.modelId,
          isActive: editingVersion.isActive,
        });
      } else {
        form.reset(defaultValues);
      }
    }
  }, [isOpen, editingVersion, form]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editingVersion ? 'Editar Versão' : 'Nova Versão'}
          </DialogTitle>
          <DialogDescription>
            Preencha os dados da versão
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ex: 1.5T Comfort" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="modelId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Modelo *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione um modelo" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {models.map((model) => (
                          <SelectItem key={model.id} value={model.id}>
                            {model.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <FormLabel>Ativo</FormLabel>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Carregando...' : (editingVersion ? 'Atualizar' : 'Criar')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
